'use client';

import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useSWRConfig } from 'swr';
import { toast } from 'sonner';
import { notifyError } from '@/lib/notify-error';
import { track, type RecurringSource } from '@/lib/telemetry';
import { ArrowsClockwiseIcon, CaretRightIcon, CircleNotchIcon, TrashIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { PageSkeleton } from '@/components/PageSkeleton';
import { RecurringPaymentCard } from '@/components/recurring-payments/RecurringPaymentCard';
import {
  canDeleteRecurringPayment,
  statusForRecurringPaymentAction,
  type RecurringPaymentAction,
} from '@/components/recurring-payments/actions';
import { PeriodSummaryCard } from '@/components/recurring-payments/PeriodSummaryCard';
import { RECURRING_GROUP_ORDER } from '@/components/recurring-payments/taxonomy';
import { CommitmentsYearChart } from '@/components/recurring-payments/CommitmentsYearChart';
import { DetectionCard } from '@/components/recurring-payments/DetectionCard';
import { HistoryDrawer } from '@/components/recurring-payments/HistoryDrawer';
import { RecurringCommitmentsCard } from '@/components/recurring-payments/RecurringCommitmentsCard';
import type { RecurringCommitment } from '@/components/recurring-payments/RecurringCommitmentRow';
import { UnconvertedChargesNotice } from '@/components/recurring-payments/UnconvertedChargesNotice';
import { RunoffChart } from '@/components/recurring-payments/RunoffChart';
import { ReviewSheet, type ReviewDecision } from '@/components/recurring-payments/ReviewSheet';
import { patchOverviewItem, reviewQueue } from '@/components/recurring-payments/reviewQueue';
import type { ConnectorBranding } from '@/components/recurring-payments/ConnectorDot';
import {
  CREDIT_COCKPIT_KEY_PREFIXES,
  swrKeyStartsWith,
} from '@/hooks/queries/swrConfig';
import { useRecurringOverview } from '@/hooks/queries/useRecurringPayments';
import { useAccounts } from '@/hooks/queries/useAccounts';
import { resolveRecurringPaymentStatus } from '@/lib/recurring/paymentStatus';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import {
  recurringPaymentsApi,
  type RecurringGroup,
  type RecurringGroupKey,
  type RecurringPayment,
  type RecurringPaymentCadence,
  type RecurringPaymentKind,
} from '@/lib/api';

function emptyGroup(key: RecurringGroupKey): RecurringGroup {
  return { key, monthlyTotal: 0, count: 0, items: [] };
}

function recurringDisplayName(item: RecurringPayment | null): string {
  return item?.merchantName ?? item?.categoryTranslated ?? item?.category ?? 'recorrente';
}

export function RecurringPaymentsView() {
  const { getToken } = useAuth();
  const { mutate: mutateGlobal } = useSWRConfig();
  const formatCurrency = useFormatCurrency();
  const [historyItem, setHistoryItem] = useState<RecurringPayment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RecurringPayment | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const openReview = () => setReviewOpen(true);

  const {
    data: overviewData,
    isLoading: overviewLoading,
    mutate: mutateOverview,
  } = useRecurringOverview();

  const { data: accounts } = useAccounts();

  const connectorFor = useCallback(
    (accountId: string | null): ConnectorBranding | null => {
      if (!accountId || !accounts) return null;
      const account = accounts.find((a) => a.id === accountId);
      if (!account) return null;
      const label = account.item?.connectorName ?? account.name ?? 'Conta';
      return {
        label,
        color: account.colorOverride ?? account.item?.connectorPrimaryColor ?? null,
        initial: label.charAt(0).toUpperCase(),
        imageUrl: account.item?.connectorImage ?? null,
      };
    },
    [accounts],
  );

  const { detected, failedCancels } = useMemo(
    () => reviewQueue(overviewData?.groups),
    [overviewData?.groups],
  );

  const groups = useMemo(() => {
    const find = (key: RecurringGroupKey) => overviewData?.groups.find((g) => g.key === key) ?? emptyGroup(key);
    return {
      parcelamentos: find('parcelamentos'),
      assinaturas: find('assinaturas'),
      fixas: find('fixas'),
    };
  }, [overviewData?.groups]);

  const totalRecorrencias =
    groups.parcelamentos.count + groups.assinaturas.count + groups.fixas.count;

  // One flat list of every commitment, each stamped with where it stands this period. Kind is a
  // filter now, not a container, so the projection has to happen once here rather than per card.
  const commitments = useMemo<RecurringCommitment[]>(() => {
    const period = overviewData?.period;
    if (!period) return [];
    return RECURRING_GROUP_ORDER.flatMap((groupKey) =>
      (overviewData.groups.find((group) => group.key === groupKey)?.items ?? []).map((item) => ({
        item,
        groupKey,
        status: resolveRecurringPaymentStatus(item, groupKey, period),
      })),
    );
  }, [overviewData]);

  // Recurring mutations change the home widgets too (Disponível subtracts the
  // commitments; the timeline marks them) — sweep those keys alongside the
  // cockpit or a new/confirmed despesa fixa won't show on the home until the
  // SWR cache ages out.
  const revalidateFinancialSurfaces = () => mutateGlobal((key) =>
    swrKeyStartsWith(key, ['home-widget', ...CREDIT_COCKPIT_KEY_PREFIXES]));

  const openHistory = (item: RecurringPayment | null) => {
    if (item) track('action.recurring.open_history', { source: 'page' });
    setHistoryItem(item);
  };

  const requestDelete = (item: RecurringPayment) => {
    if (!canDeleteRecurringPayment(item)) {
      toast.error('Parcelamentos nao podem ser excluidos');
      return;
    }
    setDeleteTarget(item);
  };

  const updateItem = async (
    id: string,
    action: RecurringPaymentAction,
    extra?: { kind?: RecurringPaymentKind; cadence?: RecurringPaymentCadence },
    source: RecurringSource = 'grid',
  ) => {
    const patch = { ...statusForRecurringPaymentAction(action), ...extra };
    await mutateOverview(
      (current) => (current ? patchOverviewItem(current, id, patch) : current),
      { revalidate: false },
    );

    try {
      const token = await getToken();
      if (!token) return;
      await recurringPaymentsApi.update(token, id, patch);
      track(`action.recurring.${action}`, { source });
    } catch (err) {
      notifyError('recurring_payments', err, 'Não foi possível atualizar o recorrente');
    } finally {
      await Promise.all([mutateOverview(), revalidateFinancialSurfaces()]);
    }
  };

  const handleReviewResolve = (
    id: string,
    decision: ReviewDecision,
    kind?: RecurringPaymentKind,
    cadence?: RecurringPaymentCadence,
  ) => {
    const extra = kind || cadence
      ? { ...(kind ? { kind } : {}), ...(cadence ? { cadence } : {}) }
      : undefined;
    void updateItem(id, decision === 'confirmed' ? 'confirm' : 'reject', extra, 'review_sheet');
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deletingId) return;
    if (!canDeleteRecurringPayment(deleteTarget)) {
      setDeleteTarget(null);
      toast.error('Parcelamentos nao podem ser excluidos');
      return;
    }

    setDeletingId(deleteTarget.id);
    try {
      const token = await getToken();
      if (!token) return;
      await recurringPaymentsApi.delete(token, deleteTarget.id);
      track('action.recurring.delete');
      if (historyItem?.id === deleteTarget.id) setHistoryItem(null);
      setDeleteTarget(null);
      await Promise.all([mutateOverview(), revalidateFinancialSurfaces()]);
      toast.success('Recorrente excluido');
    } catch (err) {
      notifyError('recurring_payments', err, 'Não foi possível excluir o recorrente');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const token = await getToken();
      if (!token) return;
      const { detected } = await recurringPaymentsApi.rescan(token);
      track('action.recurring.sync');
      await Promise.all([mutateOverview(), revalidateFinancialSurfaces()]);
      toast.success(
        detected > 0
          ? `${detected} ${detected === 1 ? 'recorrência detectada' : 'recorrências detectadas'}`
          : 'Nenhuma recorrência detectada',
      );
    } catch (err) {
      notifyError('recurring_payments', err, 'Não foi possível sincronizar as recorrências');
    } finally {
      setSyncing(false);
    }
  };

  const deleteTargetName = recurringDisplayName(deleteTarget);

  if (overviewLoading && !overviewData) return <PageSkeleton />;

  return (
    <div data-tokens="v2" className="animate-in space-y-5 duration-500 fade-in">
      <PageHeader
        title="Recorrentes"
        description={`${totalRecorrencias} ${totalRecorrencias === 1 ? 'recorrência detectada' : 'recorrências detectadas'} nas suas conexões.`}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncing}
            className="w-full gap-2 sm:w-auto"
          >
            {syncing ? (
              <CircleNotchIcon className="size-4 animate-spin" />
            ) : (
              <ArrowsClockwiseIcon className="size-4" />
            )}
            {syncing ? 'Sincronizando…' : 'Sincronizar'}
          </Button>
        }
      />

      <div data-testid="recurring-page" className="space-y-5">
      {overviewData && <PeriodSummaryCard period={overviewData.period} />}

      <UnconvertedChargesNotice items={commitments.map((commitment) => commitment.item)} />

      <DetectionCard detectedCount={detected.length} onReview={openReview} />

      {failedCancels.length > 0 && (
        <Card variant="flat" className="rounded-[var(--radius-3xl)] border-border bg-surface p-5 shadow-[var(--shadow-1)] sm:p-6">
          <h3 className="text-base font-semibold text-text-primary">Voltaram a cobrar</h3>
          <p className="mt-0.5 text-xs text-text-tertiary">Cancelamentos que voltaram a ser cobrados.</p>
          <div className="mt-4 grid gap-3">
            {failedCancels.map((item) => (
              <RecurringPaymentCard
                key={item.id}
                item={item}
                onAction={updateItem}
                onOpenHistory={openHistory}
                onDeleteRequest={requestDelete}
              />
            ))}
          </div>
        </Card>
      )}

      {totalRecorrencias === 0 ? (
        <Card variant="flat" className="rounded-[var(--radius-3xl)] border border-dashed border-border bg-surface p-8 text-center text-sm text-text-secondary shadow-[var(--shadow-1)]">
          Nenhum compromisso recorrente por aqui ainda. Sincronize suas contas para detectá-los.
        </Card>
      ) : (
        <>
          <RecurringCommitmentsCard
            commitments={commitments}
            connectorFor={connectorFor}
            onOpenItem={openHistory}
            onDeleteRequest={requestDelete}
          />

          {overviewData && overviewData.year.length > 0 && (
            <CommitmentsYearChart
              year={overviewData.year}
              currentMonthIndex={overviewData.currentMonthIndex}
            />
          )}
          <RunoffChart runoff={overviewData?.runoff ?? []} />

          <Link
            to="/recurring-payments/breakdown"
            className="flex w-full items-center justify-between rounded-[var(--radius-3xl)] border border-border bg-surface px-4 py-3.5 text-sm text-text-secondary transition-colors hover:bg-surface-sunken active:bg-divider"
          >
            Ver por categoria
            <CaretRightIcon size={15} weight="bold" className="text-text-tertiary" />
          </Link>
        </>
      )}
      </div>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deletingId) setDeleteTarget(null);
        }}
      >
        <DialogContent hideCloseButton={!!deletingId} className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-error-surface text-error-text">
                <TrashIcon size={17} weight="bold" />
              </span>
              Excluir recorrente?
            </DialogTitle>
            <DialogDescription className="text-base/6 sm:text-sm/6">
              As transacoes vinculadas continuam no historico, mas deixam de aparecer como recorrentes.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 sm:px-6">
            <div className="rounded-xl border border-border-subtle bg-surface-sunken px-3 py-2.5">
              <p className="truncate text-sm font-semibold text-text-primary" title={deleteTargetName}>
                {deleteTargetName}
              </p>
              {deleteTarget && (
                <p className="mt-0.5 font-mono text-xs text-text-secondary tabular-nums">
                  {formatCurrency(deleteTarget.amount)}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={!!deletingId}
              className="w-full sm:w-auto"
            >
              Manter
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={!!deletingId}
              className="w-full sm:w-auto"
            >
              {deletingId ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReviewSheet
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        candidates={detected}
        connectorFor={connectorFor}
        onResolve={handleReviewResolve}
      />

      <HistoryDrawer item={historyItem} onClose={() => setHistoryItem(null)} />
    </div>
  );
}
